import _ from 'lodash';
import { getCapsuleFormula } from '@/datetime/dateTime.utilities';
import { PREDICTION_OPERATION } from '@/services/calculationRunner.constants';
import { PREDICTION } from '@/tools/prediction/prediction.constants';
import { createInputsGroupArray } from '@/tools/prediction/prediction.utilities';
import { getShortIdentifier } from '@/utilities/utilities';
import type { PredictionPanelConfig } from '@/tools/prediction/predictionPanel.store';
import type { ParametersMap } from '@/utilities/formula.constants';

/**
 * Service that encapsulates logic related to construction of the prediction formula and transformation
 * of the backend table response (which has a single row) into a data structure that is more easily
 * consumed by the frontend UI.
 */

/**
 * Creates and returns a prediction or predictionModel formula along with the formula parameters and some related
 * information that is used when transforming the formula response.
 *
 * @param operator - the Seeq Formula operator that should be used (one of PREDICTION_OPERATION)
 * @param targetSignalId - The id of the dependent signal for the prediction
 * @param inputSignals - An array of the independent signals for the prediction
 * @param [conditionId] - The id of the condition that limits the prediction training window
 * @param principalComponents - The number of principal components to keep if mode is PCA
 * @param config - Object containing all the necessary information to re-populate the panel for edit
 * @param config.windowStart - a UTC unix time stamp for the start of the prediction training window
 * @param config.windowEnd - a UTC unix time stamp for the end of the prediction training window
 * @param config.option - One of PREDICTION.SCALE (e.g. LINEAR, POLYNOMIAL, LOG, etc.)
 * @param [config.polynomialValue] - The polynomial value. Only required when option is POLYNOMIAL.
 * @param config.mustGoThroughZero - Whether the prediction must be forced to go through zero.
 * @param config.lambda - The lambda value (must be positive).
 * @param config.regressionMethod - The regression method to use (one of REGRESSION_METHODS).
 * @param config.variableSelection - The variable selection (range of [0, 1]).
 * @param config.variableSelectionEnabled - True if variable selection is enabled, false otherwise.
 * @returns the formula, parameters, inputLegend (used to create the prediction model legend), and
 *   groupArray (used to replace the backend-supplied name such as "value1" with a variable name such as "$a")
 */
export function createFormula(
  operator: string,
  targetSignalId: string,
  inputSignals: { id: string; name?: string }[],
  conditionId: string | undefined,
  principalComponents: number,
  config: PredictionPanelConfig,
) {
  // Exclude the target signal from the inputs
  inputSignals = _.reject(inputSignals, (signal) => signal.id === targetSignalId);
  inputSignals = _.uniqBy(inputSignals, (signal) => signal.id);
  const inputParams = _.transform(
    inputSignals,
    (result, item, index) => {
      result[getShortIdentifier(index)] = item.id;
    },
    {},
  );
  const inputLegend = _.transform(
    inputSignals,
    (result, item, index) => {
      result[getShortIdentifier(index)] = item.name;
    },
    {},
  );

  if (_.isNil(config.regressionMethod)) {
    throw new Error('regressionMethod should be defined');
  }

  const condition = conditionId ? { condition: conditionId } : undefined;
  const parameters = _.merge({}, { target: targetSignalId }, inputParams, condition) as ParametersMap;
  const signalArray = createInputsGroupArray(inputParams, config.option, config.polynomialValue);
  const signalList = signalArray.join(', ');
  const capsuleGroup = createCapsule(config.windowStart, config.windowEnd, condition);
  const algorithm = config.regressionMethod;
  const refreshRate = config.refreshRate;
  const isRefreshRateEnabled = config.isRefreshRateEnabled;
  let modelSpecificArgument = config.mustGoThroughZero ? ', true' : ', false';
  if (algorithm === PREDICTION.REGRESSION_METHODS.RIDGE) {
    modelSpecificArgument = `, ${config.lambda}`;
  } else if (algorithm === PREDICTION.REGRESSION_METHODS.PRINCIPAL_COMPONENT_REGRESSION) {
    modelSpecificArgument = `, ${principalComponents}`;
  }

  let formula = `$target.regressionModel${algorithm}(${capsuleGroup}${modelSpecificArgument}, ${signalList})`;

  if (config.variableSelectionEnabled && _.isFinite(config.variableSelection) && config.variableSelection !== 1) {
    formula += `.variableSelection(${config.variableSelection})`;
  }

  if (isRefreshRateEnabled && !!refreshRate) {
    formula += `.setRefreshRate(${refreshRate.value}${refreshRate.units})`;
  }

  if (operator !== PREDICTION_OPERATION.PREDICTION_MODEL) {
    formula += `.predict(${signalList})`;
  }

  return {
    formula,
    parameters,
    inputLegend,
    groupArray: signalArray,
  };
}

const REGRESSION_MODEL_FORMULA_REGEX = /(\$target\.regressionModel\w+\(.*?\))\.predict/;

function findMatchingClosingParen(text: string, openingParenIndex: number): number {
  let depth = 0;
  let quote: string | undefined;

  for (let index = openingParenIndex; index < text.length; index++) {
    const character = text[index];
    const previousCharacter = text[index - 1];

    if (quote) {
      if (character === quote && previousCharacter !== '\\') {
        quote = undefined;
      }
    } else if (character === "'" || character === '"') {
      quote = character;
    } else if (character === '(') {
      depth++;
    } else if (character === ')') {
      depth--;
      if (depth === 0) {
        return index;
      }
    }
  }

  return -1;
}

/**
 * Helper method to extract the full prediction formula string from a prediction tool signal.
 *
 * @param formula The formula of the calculated signal
 * @return The formula for a prediction signal or undefined if the formula is not one
 * that produces a prediction.
 */
export function getPredictionFormula(formula: string): string | undefined {
  const regressionModelFormulaMatch = formula.match(REGRESSION_MODEL_FORMULA_REGEX) ?? { index: undefined };
  const regressionModelFormula = _.get(regressionModelFormulaMatch, '[1]');

  if (!regressionModelFormula || regressionModelFormulaMatch.index === undefined) {
    return undefined;
  }

  const predictionStartIndex = regressionModelFormulaMatch.index;
  const predictionArgumentsStartIndex = predictionStartIndex + regressionModelFormula.length + '.predict'.length;
  // Prediction arguments can contain nested function calls, so regex alone cannot reliably find the end.
  const predictionEndIndex = findMatchingClosingParen(formula, predictionArgumentsStartIndex);

  return predictionEndIndex === -1 ? undefined : formula.slice(predictionStartIndex, predictionEndIndex + 1);
}

/**
 * Helper method to extract the regression model formula string from a prediction tool signal.
 *
 * @param formula The formula of the calculated signal
 * @return The formula for a prediction signal or undefined if the formula is not one
 * that produces a regression model formula.
 */
export function getRegressionModelFormula(formula: string): string | undefined {
  return _.get(formula.match(REGRESSION_MODEL_FORMULA_REGEX), '[1]');
}

/**
 * Transforms the one-row table returned by the PREDICTION_OPERATION.PREDICTION_MODEL operation into a structure
 * more suitable for use by frontend UI code. Generates a legend that maps variable names to series names. Replaces
 * the generic * "value1" and "standardError1" coefficient labels with an actual variable name from the legend
 * (e.g. "$a")
 *
 * @param table - the prediction table returned from the backend
 * @param regressionOutput - the prediction output returned from the backend
 * @param inputSignals - an object that maps variable names to signal names
 * @param groupArray - an array containing the variable names
 * @returns {Object} - an object containing legend, coefficient, and stats information
 */
export function transformTableResponse(
  { table, regressionOutput }: { table: any; regressionOutput: any },
  inputSignals: Record<string, any>,
  groupArray: string[],
) {
  const legend = _.map(inputSignals, (name, variable) => ({
    variable: `$${variable}`,
    name,
  }));

  const stats = _.map(regressionOutput, (value, name) => ({ name, value }));

  const coefficients = _.map(groupArray, (variable, index) => ({
    name: variable,
    coefficient: table.data[index][0],
    error: table.data[index][1],
    pValue: table.data[index][2],
  }));

  return {
    legend,
    stats,
    coefficients,
  };
}

/**
 * Helper function that creates the capsule or group used in the prediction or prediction model formula
 */
export function createCapsule(start: number, end: number, condition?: { condition: string }) {
  if (_.isNil(start) || _.isNil(end)) {
    throw new Error('windowStart and windowEnd should be defined');
  }

  const capsuleRange = getCapsuleFormula({ start, end });
  return condition ? `$condition.toGroup(${capsuleRange}, CapsuleBoundary.Intersect)` : `group(${capsuleRange})`;
}
